import cn from "classnames";
import React, {useRef, useState, useEffect, useCallback} from "react";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import Cropper from "react-cropper";
import {popUpImage} from "@atoms";
import {getImagesDB} from "@api";
import useWindowDimensions from "@helpers/hooks/windowDemensions";

import axios from "axios";
import "./style.css";
import {bwFilterState, bluewhiteFilterState, universalFilterPromptState, clientCroppedImagesState, croppedImagesState, imagesData} from "../../../Recoil/Atoms";
import HeartLoader from "../../HeartLoader";
import ImageLoader from "@shared/ImageLoader";
import ExpandChoiceModal from "../ExpandChoice";
import clientLogger from '../../../../utils/clientLogger';
import { detectFaces } from '../../../../utils/faceDetection';

const ZOOM_STEP = 0.005;
// AI "magic expand" temporarily hidden — flip to re-enable the button + face-detection nudge.
const EXPAND_FEATURE_ENABLED = false;
// קלאסי / אותנטי / כפרי: a completely flat white border (no shading, no shadow
// onto the photo - it read as a separate mat) — same art as the review grid
// (style2.css). Inline, so the business-gifts editor (same classes) keeps its frames.
const FLAT_MAT_FRAME = {
  classic: "/assets/file/images/frame_classic_flat.png",
  clean: "/assets/file/images/frame_clean_flat.png",
  village: "/assets/file/images/frame_village_flat.png",
};

const EditModal = ({handleCloseModal, modalData}) => {
  const cropperRef = useRef(null);
  const {height, width} = useWindowDimensions();
  const imageElement = cropperRef?.current;
  const cropper = imageElement?.cropper;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const localstr = localStorage.getItem("uniqueUserId");
  const imageonpopup = useRecoilValue(popUpImage);
  const setImage = useSetRecoilState(popUpImage);
  const [zoomvalue, setzoomvalue] = useState(0.0);
  const [zoomMinValue, setZoomMinValue] = useState(0.0);
  const [isLoading, setLoading] = useState(true);
  const [isInitialLoaded, setIsInitialLoaded] = useState(true);
  const isInitial = useRef(true);
  // Auto-zoom factor from border detection (to avoid screenshot black bars)
  const [pendingZoomFactor, setPendingZoomFactor] = useState(1.0);
  const [images, setImages] = useRecoilState(imagesData);
  const [croppedImages, setCroppedImages] = useRecoilState(croppedImagesState);
  const [isBwFilter, setIsBwFilter] = useRecoilState(bwFilterState);
  const [isBluewhiteFilter, setIsBluewhiteFilter] = useRecoilState(bluewhiteFilterState);
  const [universalFilterPrompt, setUniversalFilterPrompt] = useRecoilState(universalFilterPromptState);
  
  // Universal filter change tracking
  const [filterChangeState, setFilterChangeState] = useState({
    originalBwFilter: null,
    originalBluewhiteFilter: null,
    currentBwFilter: false,
    currentBluewhiteFilter: false,
    hasChanges: false
  });
  const [clientCroppedImages, setClientCroppedImages] = useRecoilState(clientCroppedImagesState);
  const [showExpandModal, setShowExpandModal] = useState(false);
  // Smart-expand nudge: true when a detected face is clipped by the square crop
  const [facesCroppedOut, setFacesCroppedOut] = useState(false);
  const faceDataRef = useRef({ src: null, faces: null, imgW: 0 });

  // Does any meaningful detected face fall (mostly) outside the current square crop?
  const evaluateFaceClip = useCallback(() => {
    const fd = faceDataRef.current;
    const cropperInst = cropperRef.current?.cropper;
    if (!cropperInst || !fd.faces) return;
    // Rotation breaks the plain box comparison — don't nudge in that case.
    if (parseFloat(imageonpopup?.rotate)) { setFacesCroppedOut(false); return; }
    let crop;
    try { crop = cropperInst.getData(true); } catch (e) { return; }
    if (!crop || !crop.width) return;
    const cx1 = crop.x, cy1 = crop.y, cx2 = crop.x + crop.width, cy2 = crop.y + crop.height;
    const MIN_FACE_FRAC = 0.04; // ignore tiny background faces
    const CLIP_TOL = 0.15;      // >=15% of a face outside the crop => "cropped out"
    const clipped = fd.faces.some((f) => {
      if ((f.score ?? 1) < 0.5) return false;
      const [fx, fy, fw, fh] = f.box;
      if (fw < fd.imgW * MIN_FACE_FRAC) return false;
      const area = fw * fh;
      if (area <= 0) return false;
      const iw = Math.max(0, Math.min(fx + fw, cx2) - Math.max(fx, cx1));
      const ih = Math.max(0, Math.min(fy + fh, cy2) - Math.max(fy, cy1));
      return 1 - (iw * ih) / area >= CLIP_TOL;
    });
    setFacesCroppedOut(clipped);
  }, [imageonpopup?.rotate]);

  // On open / image change: detect faces once, then check against the crop box.
  useEffect(() => {
    let cancelled = false;
    if (!EXPAND_FEATURE_ENABLED) return undefined;
    setFacesCroppedOut(false);
    const src = imageonpopup?.image;
    faceDataRef.current = { src, faces: null, imgW: 0 };
    if (!src || !imageonpopup?._id) return undefined;
    (async () => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = src;
        if (img.decode) { await img.decode(); }
        else { await new Promise((res, rej) => { img.onload = res; img.onerror = rej; }); }
        if (cancelled) return;
        const data = await detectFaces(img);
        if (cancelled) return;
        faceDataRef.current = { src, faces: data?.faces || [], imgW: img.naturalWidth || 0 };
        evaluateFaceClip();
      } catch (e) {
        if (!cancelled) setFacesCroppedOut(false);
      }
    })();
    return () => { cancelled = true; };
  }, [imageonpopup?.image, imageonpopup?._id, evaluateFaceClip]);

  // Add a ref to track if component is mounted
  const isMounted = useRef(true);
  
  // Add cleanup in useEffect
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Universal filter change detection
  const detectFilterChange = () => {
    const { originalBwFilter, originalBluewhiteFilter } = filterChangeState;
    const currentBwFilter = isBwFilter;
    const currentBluewhiteFilter = isBluewhiteFilter;

    // Determine what changed
    if (originalBwFilter !== currentBwFilter || originalBluewhiteFilter !== currentBluewhiteFilter) {
      let changeType, filterType;

      if (!originalBwFilter && !originalBluewhiteFilter) {
        if (currentBwFilter) {
          changeType = 'ADD';
          filterType = 'bw';
        } else if (currentBluewhiteFilter) {
          changeType = 'ADD';
          filterType = 'bluewhite';
        }
      } else if (originalBwFilter) {
        if (!currentBwFilter && !currentBluewhiteFilter) {
          changeType = 'REMOVE';
          filterType = 'bw';
        } else if (currentBluewhiteFilter) {
          changeType = 'ADD';
          filterType = 'bluewhite';
        }
      } else if (originalBluewhiteFilter) {
        if (!currentBwFilter && !currentBluewhiteFilter) {
          changeType = 'REMOVE';
          filterType = 'bluewhite';
        } else if (currentBwFilter) {
          changeType = 'ADD';
          filterType = 'bw';
        }
      }

      return { changeType, filterType, hasChange: true };
    }

    return { hasChange: false };
  };

  const shouldShowPrompt = (changeType, filterType) => {
    const otherImages = images.filter(img => img._id !== imageonpopup._id);

    if (changeType === 'ADD') {
      return otherImages.some(img => !img[`${filterType}Filter`]);
    } else if (changeType === 'REMOVE') {
      return otherImages.some(img => img[`${filterType}Filter`]);
    }

    return false;
  };

  useEffect(() => {
    const correspondingImage = images.find(image => image.publicId === imageonpopup.id);
    if (correspondingImage) {
      setImage(correspondingImage);
    }
  }, [images]);

  useEffect(() => {
    cropper?.zoomTo(+imageonpopup.zoomvalue);
    cropper?.rotate(+imageonpopup.rotate);
    setIsBwFilter(imageonpopup.bwFilter);
    setIsBluewhiteFilter(imageonpopup.bluewhiteFilter);

    // Initialize universal filter change tracking
    setFilterChangeState({
      originalBwFilter: imageonpopup.bwFilter || false,
      originalBluewhiteFilter: imageonpopup.bluewhiteFilter || false,
      currentBwFilter: imageonpopup.bwFilter || false,
      currentBluewhiteFilter: imageonpopup.bluewhiteFilter || false,
      hasChanges: false
    });
  }, []);

  const handleFilterUpdate = async () => {
    try {
      const localstr = localStorage.getItem("uniqueUserId");
      const userEmail = localStorage.getItem("userEmail") || "";
      const sessionId = localStorage.getItem("sessionId");

      const endpoint = isBwFilter ? 'add-bw-filter' : 'remove-bw-filter';
      await axios.post(`${BASE_URL}/${endpoint}/${localstr}`, {
        email: userEmail,
        sessionId: sessionId || ""
      });

      await getImagesDB();
    } catch (err) {
      console.error(`Error updating B&W filter: ${err.message}`);
    }
  }

  const handleBluewhiteFilterUpdate = async () => {
    try {
      const localstr = localStorage.getItem("uniqueUserId");
      const userEmail = localStorage.getItem("userEmail") || "";
      const sessionId = localStorage.getItem("sessionId");

      const endpoint = isBluewhiteFilter ? 'add-bluewhite-filter' : 'remove-bluewhite-filter';
      await axios.post(`${BASE_URL}/${endpoint}/${localstr}`, {
        email: userEmail,
        sessionId: sessionId || ""
      });

      await getImagesDB();
    } catch (err) {
      console.error(`Error updating Bluewhite filter: ${err.message}`);
    }
  }

  const calCropData = (imageonpopup) => {
    // If cropbox_data has naturalWidth, it came from a previous edit (full cropper data)
    if (imageonpopup.cropbox_data.naturalWidth) {
      return { ...imageonpopup.cropbox_data };
    }

    // Server-saved cropbox_data is in extract coordinates - convert to cropper moveTo coordinates
    let left = imageonpopup.cropbox_data.left;
    let top = imageonpopup.cropbox_data.top;
    const cropSize = imageonpopup.cropbox_data.width;
    const zoomFactor = imageonpopup.cropbox_data.zoomFactor || 1.0;
    const naturalHeight = parseInt(imageonpopup.imageheight);
    const naturalWidth = parseInt(imageonpopup.imagewidth);
    const croperSize = getCropperSize();

    // Calculate scale rate - cropper fits the smaller dimension to the crop box
    let rate;
    if (naturalHeight <= naturalWidth) {
      rate = croperSize / naturalHeight;
    } else {
      rate = croperSize / naturalWidth;
    }

    // When there's a zoomFactor > 1.0, we need to position the cropper to show the viewport
    // (the smaller region that was extracted and scaled up to avoid borders)
    if (zoomFactor > 1.0 && cropSize) {
      const zoomedRate = rate * zoomFactor;

      // Use the stored viewport if available (calculated during face detection)
      // This is the exact viewport that was used to create the auto-cropped preview
      const storedViewport = imageonpopup.cropbox_data.viewport;

      let viewportX, viewportY, viewportSize;
      if (storedViewport && storedViewport.x !== undefined) {
        const scaleFactor = naturalWidth / cropSize;
        viewportX = storedViewport.x * scaleFactor;
        viewportY = storedViewport.y * scaleFactor;
        viewportSize = storedViewport.width * scaleFactor;
      } else {
        // Fallback: calculate viewport
        viewportSize = naturalWidth / zoomFactor;
        const scaledCropSize = naturalWidth;
        const scaledLeft = left * (naturalWidth / cropSize);
        const scaledTop = top * (naturalWidth / cropSize);
        const centerX = scaledLeft + scaledCropSize / 2;
        const centerY = scaledTop + scaledCropSize / 2;
        viewportX = centerX - viewportSize / 2;
        viewportY = centerY - viewportSize / 2;
      }

      // Convert viewport position to cropper moveTo coordinates
      left = viewportX * -zoomedRate;
      top = viewportY * -zoomedRate;
    } else {
      // No zoom - convert extract coordinates to moveTo coordinates
      left = left * -rate;
      top = top * -rate;
    }

    clientLogger.debug(`[calCropData] cropbox_data:`, {
      left: imageonpopup.cropbox_data.left,
      top: imageonpopup.cropbox_data.top,
      width: imageonpopup.cropbox_data.width,
      zoomFactor,
      source: imageonpopup.cropbox_data.source
    });
    clientLogger.debug(`[calCropData] imageWidth=${naturalWidth}, imageHeight=${naturalHeight}, cropperSize=${croperSize}, rate=${rate}`);
    clientLogger.debug(`[calCropData] moveTo: left=${left}, top=${top}`);

    return {
      top,
      left,
    };
  };
  const InitialSets = (target) => {
    const savedZoom = +imageonpopup.zoomvalue;
    const autoZoomFactor = imageonpopup.cropbox_data?.zoomFactor || 1.0;
    const wasManuallyEdited = imageonpopup.cropbox_data?.naturalWidth !== undefined;

    target?.rotate(+imageonpopup.rotate);

    if (!imageonpopup.cropbox_data) {
      // No crop data (e.g. after expand) — let cropper use default centered position
      clientLogger.debug(`[InitialSets] No cropbox_data, using cropper defaults`);

    } else if (savedZoom > 0.001 && wasManuallyEdited) {
      // User has manually zoomed before - use their saved value
      target?.zoomTo(savedZoom);
      setzoomvalue(savedZoom);
      const cropData = calCropData(imageonpopup);
      target.moveTo(cropData.left, cropData.top);

    } else if (autoZoomFactor > 1.0) {
      // Apply auto-zoom to avoid borders
      const canvasData = target.getCanvasData();
      const imageData = target.getImageData();
      const minZoom = canvasData.width / imageData.naturalWidth;
      const autoZoom = minZoom * autoZoomFactor;

      target.zoomTo(autoZoom);
      setzoomvalue(autoZoom);
      setZoomMinValue(minZoom);
      window.sessionStorage.setItem("minZoomLevel", minZoom);

      const cropData = calCropData(imageonpopup);
      target.moveTo(cropData.left, cropData.top);

    } else {
      // No zoom adjustment needed
      const canvasData = target.getCanvasData();
      const imageData = target.getImageData();
      clientLogger.debug(`[InitialSets] No zoom. Canvas:`, canvasData, `Image natural:`, imageData.naturalWidth, imageData.naturalHeight);
      clientLogger.debug(`[InitialSets] Cropper actual minZoom = ${canvasData.width / imageData.naturalWidth}`);
      const cropData = calCropData(imageonpopup);
      clientLogger.debug(`[InitialSets] moveTo(${cropData.left}, ${cropData.top})`);
      target.moveTo(cropData.left, cropData.top);
    }

    if (isMounted.current) {
      setLoading(false);
      setIsInitialLoaded(false);
    }
  };

  const getCropData = async () => {
    setLoading(true);

    // Use universal filter change detection
    const changeInfo = detectFilterChange();

    // Prepare prompt data
    let promptData = null;
    if (changeInfo.hasChange) {
      const needsPrompt = shouldShowPrompt(changeInfo.changeType, changeInfo.filterType);
      if (needsPrompt) {
        promptData = {
          changeType: changeInfo.changeType,
          filterType: changeInfo.filterType,
          changedImageId: imageonpopup._id
        };
      }
    }
    
    // Legacy prompt flags removed - using universal prompt system only
    
    // Add the image to croppedImages before closing the modal
    setCroppedImages([
      ...croppedImages,
      imageonpopup,
    ]);
    
    // We'll close the modal after preparing all the data
    if (typeof cropper !== "undefined") {
      // Get all the data we need from the cropper
      var cropbox_data = cropper.getCanvasData();
      var image_data = cropper.getImageData();
      var crop_data = cropper.getData(); // This gives the precise crop coordinates
      var rotate = 0;
      
      if (image_data.rotate === undefined) {
        rotate = 0;
      } else {
        rotate = image_data.rotate;
      }
      
      // Get the exact visible portion
      const visiblePortion = {
        x: crop_data.x,
        y: crop_data.y,
        width: crop_data.width,
        height: crop_data.height,
        scaleX: image_data.scaleX,
        scaleY: image_data.scaleY,
        naturalWidth: image_data.naturalWidth,
        naturalHeight: image_data.naturalHeight
      };
      
      // Generate a client-side cropped image
      let croppedImageDataUrl = null;
      try {
        // Calculate the optimal size for the cropped canvas based on the original image dimensions
        // We want to maintain the original resolution while ensuring it's a square and not exceeding 2000x2000
        const originalWidth = image_data.naturalWidth;
        const originalHeight = image_data.naturalHeight;
        
        // Determine the optimal size (the largest dimension, but not exceeding 2000)
        const maxSize = Math.min(2000, Math.max(originalWidth, originalHeight));
        
        // Get the cropped canvas at the optimal size
        const croppedCanvas = cropper.getCroppedCanvas({
          width: maxSize,
          height: maxSize,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        });
        
        // IMPORTANT: Do NOT apply filters to the canvas directly
        // Filters should only be applied via CSS for toggle-ability
        // The client-side crop should be a raw, unfiltered image
        
        // Convert to data URL with compression to reduce size
        croppedImageDataUrl = croppedCanvas.toDataURL('image/jpeg', 0.85);
        
        // Store in Recoil state for future use
        setClientCroppedImages({
          ...clientCroppedImages,
          [imageonpopup._id]: croppedImageDataUrl
        });
      } catch (error) {
        console.error("Failed to generate client-side cropped image:", error);
      }

      const userEmail = localStorage.getItem("userEmail") || "";

      const aa = {
        uid: localstr,
        email: userEmail,
        bwFilter: isBwFilter,
        bluewhiteFilter: isBluewhiteFilter,
        id: imageonpopup?._id,
        cropbox_data: cropbox_data,
        image_data: image_data,
        crop_data: crop_data,
        visiblePortion: visiblePortion,
        rotate: rotate,
        zoomvalue: (zoomvalue === 0) ? 0.01 : zoomvalue,
        defaultSize: getCropperSize(),
        clientCroppedImage: croppedImageDataUrl,
        // Send current image URL and dimensions so server persists expanded images
        image: imageonpopup.image,
        imagewidth: imageonpopup.imagewidth,
        imageheight: imageonpopup.imageheight,
      };

      // Add sessionId to request if available
      const sessionId = localStorage.getItem("sessionId");
      if (sessionId) {
        aa.sessionId = sessionId;
      }

      const config = {
        headers: {
          "content-type": "application/json",
        },
      };

      try {
        await axios.post(BASE_URL + "/cropped_img", aa, config);
        setCroppedImages(prev => prev.filter(img => img._id !== imageonpopup._id));
        handleCloseModal("editImage");
        await getImagesDB();

        if (isMounted.current) {
          setLoading(false);

          if (promptData) {
            setUniversalFilterPrompt({
              show: true,
              changeType: promptData.changeType,
              filterType: promptData.filterType,
              changedImageId: promptData.changedImageId
            });
          }
        }
      } catch (error) {
        console.error("Error saving crop:", error.message);
        setCroppedImages(prev => prev.filter(img => img._id !== imageonpopup._id));
        handleCloseModal("editImage");

        if (isMounted.current) {
          setLoading(false);
          if (promptData) {
            setUniversalFilterPrompt({
              show: true,
              changeType: promptData.changeType,
              filterType: promptData.filterType,
              changedImageId: promptData.changedImageId
            });
          }
        }
      }
    }
  };

  const getZoomLevel = useCallback((imageHeight, imageWidth) => {
    const maxImageDimension = Math.max(imageHeight, imageWidth);
    const zoomMin = +window.sessionStorage.getItem("minZoomLevel");
    if (maxImageDimension >= 1600) {
      return 2 * zoomMin;
    } else if (maxImageDimension >= 1400) {
      return 1.6 * zoomMin;
    } else if (maxImageDimension >= 1200) {
      return 1.4 * zoomMin;
    } else if (maxImageDimension >= 800) {
      return 1.2 * zoomMin;
    } else {
      return 0.0;
    }
  }, []);

  const handleOnZoom = useCallback((e) => {
    if (isInitial.current) {
      const minZoom = +e.detail.oldRatio;

      if (isMounted.current) {
        setZoomMinValue(minZoom);
      }
      window.sessionStorage.setItem("minZoomLevel", minZoom);
      isInitial.current = false;

      const autoZoomFactor = imageonpopup.cropbox_data?.zoomFactor || 1.0;
      const wasManuallyEdited = imageonpopup.cropbox_data?.naturalWidth !== undefined;

      if (!imageonpopup.cropbox_data) {
        // No crop data (e.g. after expand) — skip repositioning, use cropper defaults
        clientLogger.debug(`[handleOnZoom] No cropbox_data, skipping reposition`);
      } else if (autoZoomFactor > 1.0 && !wasManuallyEdited) {
        // Apply auto-zoom if borders detected and user hasn't manually edited
        const autoZoom = minZoom * autoZoomFactor;
        setTimeout(() => {
          if (isMounted.current && cropperRef.current?.cropper) {
            const cropper = cropperRef.current.cropper;
            cropper.zoomTo(autoZoom);
            setzoomvalue(autoZoom);

            const cropData = calCropData(imageonpopup);
            cropper.moveTo(cropData.left, cropData.top);
          }
        }, 50);
      } else if (!wasManuallyEdited) {
        // No auto-zoom needed, but still reposition after cropper settles
        // (moveTo in InitialSets fires before cropper fully renders)
        setTimeout(() => {
          if (isMounted.current && cropperRef.current?.cropper) {
            const cropper = cropperRef.current.cropper;
            const canvasData = cropper.getCanvasData();
            const imageData = cropper.getImageData();
            clientLogger.debug(`[handleOnZoom] setTimeout reposition. Canvas:`, canvasData);
            clientLogger.debug(`[handleOnZoom] Image natural: ${imageData.naturalWidth}x${imageData.naturalHeight}`);
            clientLogger.debug(`[handleOnZoom] Stored imagewidth=${imageonpopup.imagewidth}, imageheight=${imageonpopup.imageheight}`);
            const cropData = calCropData(imageonpopup);
            clientLogger.debug(`[handleOnZoom] moveTo(${cropData.left}, ${cropData.top})`);
            cropper.moveTo(cropData.left, cropData.top);
          }
        }, 50);
      }
    } else {
      if (imageonpopup.imagewidth < 800 && imageonpopup.imageheight < 800) {
        e.preventDefault();
      }
      if (e.detail.ratio >= getZoomLevel(+imageonpopup.imageheight, +imageonpopup.imagewidth)) {
        e.preventDefault();
      } else {
        if (isMounted.current) {
          setzoomvalue(e.detail.ratio);
        }
      }
    }
  }, [getZoomLevel, imageonpopup]);

  const handleZoomTo = (e) => {
    if (isMounted.current) {
      setzoomvalue(+e.target.value);
    }
    cropper?.zoomTo(+e.target.value);
  };

  const zoomvalueplus = () => {
    if (imageonpopup.imagewidth < 800 && imageonpopup.imageheight < 800) return;
    if (zoomvalue >= getZoomLevel(+imageonpopup.imageheight, +imageonpopup.imagewidth)) {
      return;
    } else {
      if (isMounted.current) {
        setzoomvalue((zm) => zm + ZOOM_STEP);
      }
      cropper.zoomTo((zoomvalue + ZOOM_STEP));
    }
  };

  const zoomvalueminus = () => {
    if (zoomvalue < zoomMinValue + ZOOM_STEP) {
      if (isMounted.current) {
        setzoomvalue(zoomMinValue);
      }
      cropper.zoomTo(zoomMinValue);
    } else {
      if (isMounted.current) {
        setzoomvalue((zm) => zm - ZOOM_STEP);
      }
      cropper.zoomTo((zoomvalue - ZOOM_STEP));
    }
  };
  const isLowImgSize = +imageonpopup.imageheight >= 800 && +imageonpopup.imagewidth >= 800;
  const getCropperSize = () => ({
    classic: 252,
    clean: 252,
    bold: 252,
    ever: 252,
    nordic: 252,
    village: 252,
  }[modalData.frame]);

  const el = document.querySelector(".cropper-container");

  if (el) {
    switch (modalData?.frame) {
      case "bold":
      case "nordic":
        el.style.cssText += "transform: scale(1.35) translateY(33px);";
        break;
      case "ever":
        el.style.cssText += "transform: scale(1.37) translateY(33px);";
        break;
      case "clean":
        el.style.cssText += "transform: scale(1.07) translateY(7px);";
        break;
    }
  }

  const handleFilterClick = () => {
    if (isMounted.current) {
      const newBwState = !isBwFilter;
      setIsBwFilter(newBwState);
      setIsBluewhiteFilter(false); // Mutual exclusion

      setFilterChangeState({
        ...filterChangeState,
        currentBwFilter: newBwState,
        currentBluewhiteFilter: false,
        hasChanges: true
      });
    }
  };

  const handleBluewhiteFilterClick = () => {
    const turningOn = !isBluewhiteFilter;

    if (isMounted.current) {
      setIsBluewhiteFilter(turningOn);
      setIsBwFilter(false); // Mutual exclusion

      setFilterChangeState({
        ...filterChangeState,
        currentBwFilter: false,
        currentBluewhiteFilter: turningOn,
        hasChanges: true
      });
    }
  };


  return (
    <div id="edit-modal">
      {width > 767 ?
        (<div className="edit-modal__header">
          <button disabled={!isInitialLoaded && isLoading} onClick={() => getCropData()} className="edit">
            <>
              סיום
            </>
          </button>
          <button onClick={() => {
            // Reset universal filter state
            setUniversalFilterPrompt({ show: false, changeType: null, filterType: null, changedImageId: null });
            handleCloseModal("editImage");
          }} className="close">
            <img src="/assets/file/images/cross.svg" alt="close" />
          </button>
        </div>)
        : (<div className="edit-modal__header-moblow">
          <p>עריכת תמונה (21x21)</p>
        </div>)
      }
      <div className="edit-modal__content">

        {width > 767 && (
          <div style={{display: 'flex', flexDirection: 'column', gap: '6px', position: 'absolute', marginLeft: '75px', marginTop: '15px'}}>
            <div className="black-white-filter" onClick={handleFilterClick} style={{position: 'relative', margin: '0'}}>
              <img
                src={`/assets/images/bw-filter-button${isBwFilter ? "-clicked" : ""}.svg`}
                alt="black and white filter"
              />
            </div>
            {/* <div className="bluewhite-filter" onClick={handleBluewhiteFilterClick} style={{position: 'relative', margin: '0'}}>
              <img
                src={`/assets/images/bluewhite-filter${isBluewhiteFilter ? "-clicked" : ""}.svg`}
                alt="bluewhite filter"
              />
            </div> */}
            {EXPAND_FEATURE_ENABLED && (
            <div className="expand-btn-wrap">
              <button
                type="button"
                className="expand-trigger-btn"
                onClick={() => setShowExpandModal(true)}
                title="הרחבה קסומה"
                aria-label="הרחבה קסומה"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.4c.55 5.6 2.1 7.15 7.6 7.6-5.5.45-7.05 2-7.6 7.6-.55-5.6-2.1-7.15-7.6-7.6 5.5-.45 7.05-2 7.6-7.6Z" />
                  <path d="M19 14.6c.2 2.1.85 2.75 3 3-2.15.25-2.8.9-3 3-.2-2.1-.85-2.75-3-3 2.15-.25 2.8-.9 3-3Z" />
                </svg>
              </button>
              {facesCroppedOut && (
                <span className="expand-nudge expand-nudge--desktop">זיהינו שאולי תרצו להרחיב את התמונה</span>
              )}
            </div>
            )}
          </div>
        )}
        <div className="modal-title-mob">
          {isLowImgSize ? <p style={{fontWeight: "400", fontSize: "14px"}}>
          <span style={{fontSize: "18px", fontWeight: "500"}}>הזזה והגדלה</span>
          <br/>
              {/* <img src="/assets/images/editpinch.svg" /> */}
              צבטו כדי להגדיל ולהזיז
            </p> :
            <p>
              <img src="/assets/images/editmove.svg" />
          ניתן להזיז את התמונה בתוך המסגרת
            </p>}
        </div>
        <div className={`${modalData?.frame} cropper-frame`}
             style={{
               marginTop: "24px",
               boxShadow: "0 3px 7px rgb(0 0 0 / 5%)",
               ...(FLAT_MAT_FRAME[modalData?.frame] && { backgroundImage: `url(${FLAT_MAT_FRAME[modalData?.frame]})` }),
             }}>
          {imageonpopup._id ? (
            <>
              {isInitialLoaded && (
                <>
                  <div id={`${width > 767 ? "deskedit-in-frame-loader" : "mobiledit-in-frame-loader"}`} />
                </>
              )}
              {/* filter is applied to .cropper-container via these classes (style.css), not to this div:
                  .cropper-container is transform-enlarged past this div's box and iOS left the overflow in colour */}
              <div className={`${isBwFilter ? "edit-cropper--bw" : ""} ${isBluewhiteFilter ? "edit-cropper--bluewhite" : ""}`}>
                <Cropper
                  ref={cropperRef}
                  style={{
                    height: getCropperSize(),
                    width: getCropperSize(),
                    transform: "rotationimage",
                    margin: "0 auto",
                  }}
                  zoomable
                  initialAspectRatio={1}
                  preview=".img-preview"
                  checkCrossOrigin={true}
                  src={`${imageonpopup.image}`}
                  viewMode={3}
                  autoCropArea={1}
                  guides={false}
                  toggleDragModeOnDblclick
                  ready={(e) => {
                    InitialSets(e.target.cropper);
                    evaluateFaceClip();
                  }}
                  cropend={() => evaluateFaceClip()}
                  dragMode="move"
                  background
                  responsive
                  checkOrientation={false}
                  zoom={handleOnZoom}
                  center={false}
                />
              </div>
            </>
          ) : (
            <>
              <div className={`album-item-wrapper`}
                   style={{margin: "auto", width: "100%", height: "100%", filter: "none"}}>
                <ImageLoader
                  loadingBlob={imageonpopup.url}
                  loading={modalData.imagesBlob.includes(imageonpopup)}
                  src={imageonpopup.url ? imageonpopup.url : imageonpopup.image}
                  loaderClassName={cn(`img-con blurred-loader-image`)}
                  className={cn(`img-con edit blurred-loader-image edit`)}
                  alt="icon"
                  shadow={true}
                  style={{padding: "0"}}
                />
              </div>
            </>
          )}
        </div>
        {isLowImgSize ? <div className="rang_container">
          <div
            onClick={() => {
              zoomvalueminus();
            }}
          />
          <input
            type="range"
            min={`${zoomMinValue}`}
            max={`${getZoomLevel(+imageonpopup.imageheight, +imageonpopup.imagewidth)}`}
            value={zoomvalue}
            onChange={handleZoomTo}
            step={`${ZOOM_STEP}`}
            id="zoomer"
            className="slider"
          />
          <img
            src="/assets/file/images/plus_icon.svg"
            onClick={() => {
              zoomvalueplus();
            }}
            alt="plus-icon"
          />
        </div> : null}

        <div className="modal-title-desk">
          {isLowImgSize ?
            <p style={{fontWeight: "500", fontSize: "14px"}}>
              הזיזו והגדילו בעזרת העכבר
            </p> :
            <p>
ניתן להזיז את התמונה בתוך המסגרת בעזרת העכבר
            </p>}
        </div>
        {width < 767 && (
          <div style={{
            position: 'absolute',
            left: '6%',
            bottom: '72px', // just above the confirm button (which is usually at bottom: 16px or 24px)
            display: 'flex',
            flexDirection: 'row',
            gap: 6,
            zIndex: 10
          }}>
<div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: '6px' }}>
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {/* Invisible placeholder to maintain height when no tag is shown */}
    <div style={{ height: '28px', display: 'flex', alignItems: 'flex-end' }}>
      {/* Uncomment when you want to show the tag */}
      {/* <span className="filter-tag-bluewhite-mobile">חדש</span> */}
    </div>
    {/* <img
      className="filter-icon-bluewhite-mobile"
      src={`/assets/images/bluewhite-filter${isBluewhiteFilter ? "-clicked" : ""}.svg`}
      alt="פילטר כחול לבן"
      onClick={handleBluewhiteFilterClick}
      style={{ cursor: 'pointer', width: 40, height: 40 }}
    /> */}
  </div>
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <div style={{ height: '28px', display: 'flex', alignItems: 'flex-end' }}>
      <span className="filter-tag-greyscale-mobile">פופולארי</span>
    </div>
    <img
      src={`/assets/images/bw-filter-button${isBwFilter ? "-clicked" : ""}.svg`}
      alt="תמונות בשחור לבן"
      onClick={handleFilterClick}
      style={{ cursor: 'pointer', width: 40, height: 40 }}
    />
  </div>
  {EXPAND_FEATURE_ENABLED && (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
    {/* Empty placeholder keeps icon baseline aligned with the tagged B&W button */}
    <div style={{ height: '28px' }} />
    {facesCroppedOut && (
      <span className="expand-nudge expand-nudge--mobile">זיהינו שאולי תרצו להרחיב את התמונה</span>
    )}
    <button
      type="button"
      className="expand-trigger-btn expand-trigger-btn--mobile"
      onClick={() => setShowExpandModal(true)}
      title="הרחבה קסומה"
      aria-label="הרחבה קסומה"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 2.4c.55 5.6 2.1 7.15 7.6 7.6-5.5.45-7.05 2-7.6 7.6-.55-5.6-2.1-7.15-7.6-7.6 5.5-.45 7.05-2 7.6-7.6Z" />
        <path d="M19 14.6c.2 2.1.85 2.75 3 3-2.15.25-2.8.9-3 3-.2-2.1-.85-2.75-3-3 2.15-.25 2.8-.9 3-3Z" />
      </svg>
    </button>
  </div>
  )}
</div>
</div>
        )}
        {width <= 767 && (
          <button className="mobile-save-button" onClick={() => getCropData()}>
            {isLoading && !isInitialLoaded ? (
              <>
                <div className="ldio-kv0ui0pfesk-edit" />
              </>
            ) : (
              <>
                אישור
              </>
            )}
          </button>
        )}
      </div>

      {showExpandModal && (
        <ExpandChoiceModal
          imageUrl={imageonpopup.image}
          imagePublicId={imageonpopup.publicId}
          frame={modalData?.frame}
          onClose={() => setShowExpandModal(false)}
          onSelect={(newImageUrl, expandedWidth, expandedHeight) => {
            const updatedFields = {
              image: newImageUrl,
              crop_image: newImageUrl,
              cropbox_data: null,
              zoomvalue: 0,
              imagewidth: expandedWidth ? String(expandedWidth) : imageonpopup.imagewidth,
              imageheight: expandedHeight ? String(expandedHeight) : imageonpopup.imageheight,
            };

            // Update popUpImage (edit modal)
            setImage({ ...imageonpopup, ...updatedFields });

            // Update imagesData (grid) so dimensions and crop_image sync immediately
            setImages(prev => prev.map(img =>
              img._id === imageonpopup._id ? { ...img, ...updatedFields } : img
            ));

            // Clear old client crop so grid uses the new crop_image URL
            // Remove by both _id and publicId since the grid checks both keys
            const imgId = imageonpopup._id;
            const pubId = imageonpopup.publicId;
            setClientCroppedImages(prev => {
              if (!prev) return {};
              const updated = { ...prev };
              if (imgId) delete updated[imgId];
              if (pubId) delete updated[pubId];
              return updated;
            });

            setShowExpandModal(false);
          }}
        />
      )}
    </div>
  );
};

export default EditModal;
